import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-scroll';

import {
	Wrapper,
	ReviewCount,
} from './ratingStarsStyles';

import StarHalf from 'core/icons/StarHalf';
import { plural } from 'core/utils/stringUtils';

/**
 * Return a range of numbers from 1...size
 * e.g. `range(31)` will return `["1",...,"31"]`
 * @param {Number} size
 * @return {Array} Array of numbers from 1...size
 */
function range(size) {
	return [...Array(size).keys()].map(i => (i + 1).toString());
}

/**
 * Split decimal amount of stars into
 * whole number amount of stars, half stars, and empty stars
 *
 * @param {Float} stars - actual num stars
 * @param {Number} max - possible num of stars
 * @return {Array[Number]} [fullStars, halfStars, emptyStars]
 */
function splitRating(stars, max = 5) {
	if (stars >= max) return [max, 0, 0];

	// upperBound & lowerBound define the inclusive range of a halfStar
	const upperBound = 0.54, lowerBound = 0.1;
	let fullStars = Math.floor(stars);
	const decimal = stars - fullStars;
	const halfStars = decimal >= lowerBound && decimal <= upperBound ? 1 : 0;
	if (decimal > upperBound) {
		fullStars += 1;
	}
	const emptyStars = max - (fullStars + halfStars);

	return [fullStars, halfStars, emptyStars];
}

const ReviewNumber = ({ children, slug, anchorTo }) => {
	let props = {};
	if (!anchorTo && slug) {
		props = {
			as: "a",
			href: `/subscription-box/${slug}/reviews/`,
		};
	}
	return (
		<ReviewCount {...props}>
			{children}
		</ReviewCount>
	);
};

ReviewNumber.propTypes = {
	slug: PropTypes.string,
	anchorTo: PropTypes.string,
	children: PropTypes.node.isRequired,
};

const RatingStars = ({
	slug,
	stars,
	reviews,
	stacked,
	tiny,
	short,
	anchorTo,
	starsOnly,
	noStars,
	starColor,
}) => {
	const showCount = !!reviews && !starsOnly;
	const [fullStars, halfStars, emptyStars] = splitRating(stars || 0);
	const halfStarDims = tiny ? 10 : 14;
	const wrapperProps = !anchorTo ? {} : {
		as: Link,
		to: anchorTo,
		smooth: true,
		style: {
			cursor: 'pointer',
		},
	};
	return (
		<Wrapper $stacked={stacked} $tiny={tiny} $noStars={noStars} $color={starColor} {...wrapperProps}>
			{range(fullStars).map(x => <Fragment key={`full-star-${x}`}>{'★'}</Fragment>)}
			{halfStars > 0 ? <StarHalf width={halfStarDims} height={halfStarDims} /> : null}
			{range(emptyStars).map(x => <Fragment key={`empty-star-${x}`}>{'☆'}</Fragment>)}
			{showCount && (
				<ReviewNumber slug={slug} anchorTo={anchorTo}>
					{reviews} {!short && plural('review', reviews)}
				</ReviewNumber>
			)}
		</Wrapper>
	);
};

RatingStars.propTypes = {
	// Only pass slug if you want the count to display as link
	slug: PropTypes.string,
	stars: PropTypes.number,
	reviews: PropTypes.number,
	stacked: PropTypes.bool,
	anchorTo: PropTypes.string,
	// Affects size/styling
	tiny: PropTypes.bool,
	// True to skip displaying "reviews" text
	short: PropTypes.bool,
	// True to skip displaying reviews count
	starsOnly: PropTypes.bool,
	noStars: PropTypes.bool,
	starColor: PropTypes.string,
};

RatingStars.defaultProps = {
	wrapper: Wrapper,
	stars: 0,
	reviews: 0,
	stacked: false,
	tiny: false,
	short: false,
	starsOnly: false,
	noStars: false,
	starColor: "gold",
};

export default RatingStars;
